import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";

/** GET /org/entitlements (0097) — is this org on the FREE plan, and which gated features it has. */
export interface OrgEntitlements {
  freeTier: boolean;
  freePlanEndsAt: string | null;
  features: { adaptiveQuestions: boolean; resumeQuestions: boolean };
}

/** What we assume when the read fails (e.g. an older API without the endpoint): NOT free — the
 *  server still enforces the lock with a 402, so the UI never grants anything the API refuses. */
export const NOT_FREE: OrgEntitlements = {
  freeTier: false,
  freePlanEndsAt: null,
  features: { adaptiveQuestions: true, resumeQuestions: true },
};

export function useEntitlements(): { entitlements: OrgEntitlements; isLoading: boolean } {
  const { me } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["entitlements", me?.orgId],
    queryFn: async (): Promise<OrgEntitlements> => {
      try {
        const r = await api.get<Partial<OrgEntitlements>>("/org/entitlements");
        const freeTier = r?.freeTier === true;
        return {
          freeTier,
          freePlanEndsAt: typeof r?.freePlanEndsAt === "string" ? r.freePlanEndsAt : null,
          features: {
            adaptiveQuestions: r?.features?.adaptiveQuestions ?? !freeTier,
            resumeQuestions: r?.features?.resumeQuestions ?? !freeTier,
          },
        };
      } catch {
        return NOT_FREE;
      }
    },
    enabled: !!me?.orgId,
    staleTime: 60_000,
    retry: false,
  });
  return { entitlements: data ?? NOT_FREE, isLoading };
}
